// Card 48, the torpedo run (R548, ui-map-interface.md §17.3.48).
//
// A submarine plots a torpedo's run as a circle of its range and aims
// inside it. Here the selected own hull draws the run of the round it
// loaded, lettered along the ring; the selected own hold draws the run of
// the first round it can fire; and an armed aim draws the fuse circle where
// the round goes off.
//
// **The solid aim ring stays the fleet layer's** (`packReach`, §6.3). This
// piece adds the LETTERED dashed ring and nothing that layer already draws,
// so a reader never sees the same reach twice in two weights.
//
// **Own hulls and the own hold only.** A stranger's loaded round is not on
// the wire, and the piece must never guess one.

import { chart as S } from '../../../gen/strings';
import { FLEET_ORDER_COLORS, type ChartStep } from '../../../gen/presentation';
import { SiteKind } from '../../../gen/proto/aquaphobia';
import type { Pt } from '../../../render/text-run';
import type { FrameInfo } from '../../layer';
import { type ChartContext, type ChartSinks, type Piece } from '../context';
import { dashedRing, faceOf, inkOf } from './shared';

/** How many points a lettered ring is laid along. */
const RING_STEPS = 96;

/** One ring the piece draws: where, how big, and what is written on it. */
interface Run {
  x: number;
  y: number;
  r: number;
  text: string;
  /** The lettering angle and the ring's own alpha and dash. */
  angle: number;
  alpha: number;
  dash: readonly number[];
  /** The chart text step of the ring's words (R1796). */
  step: ChartStep;
}

export class TorpedoRunPiece implements Piece {
  readonly id = 'torpedo_run' as const;
  readonly tier = 'screen' as const;
  readonly pack = 'static' as const;
  readonly film = 'hide' as const;
  /**
   * A selection makes this piece, and a hello, a state frame or a pointer
   * move can change what the selection means: the hull reloads, the hold
   * stocks a different round, the reader arms an aim.
   */
  readonly rebuildOn = [
    'selection', 'hover', 'state', 'sites', 'hello', 'reading', 'zoomband',
    'font',
  ] as const;

  private runs: Run[] = [];
  /** The armed aim, or null. */
  private aim: { x: number; y: number; fromX: number; fromY: number } | null = null;

  /** Nothing draws while neither a run nor an aim stands. */
  visible(ctx: ChartContext): boolean {
    return ctx.selection !== null || ctx.aim !== null;
  }

  rebuild(ctx: ChartContext): void {
    const t = ctx.cfg.pieces.torpedo_run;
    const r = t[ctx.reading];
    this.runs = [];
    this.aim = null;
    const sel = ctx.selection;
    const fleets = ctx.scene.fleets;
    let fromX = ctx.scene.home.x, fromY = ctx.scene.home.y;
    if (sel?.kind === 'fleet' && fleets) {
      for (const f of fleets.chartFleets()) {
        if (f.id !== sel.id || !f.own) continue;
        fromX = f.x; fromY = f.y;
        if (f.torpedoRange > 0) {
          const kind = ctx.state.torpedo(f.firingKind)?.name ?? '';
          const px = Math.round(f.torpedoRange);
          this.runs.push({
            x: f.x, y: f.y, r: f.torpedoRange,
            text: ctx.reading === 'historical' ? S.run_historical : S.run_modern({ kind, px }),
            angle: r.fig.ring_angle, alpha: t.fig.ring_alpha,
            dash: r.dash, step: t.text.main,
          });
        }
      }
    }
    if (this.ownHold(ctx)) {
      // The hold's loaded kind is not on the wire, so the ring is the first
      // round the hold can fire at all (R86).
      const round = ctx.state.stockedHoldRounds()[0];
      if (round && round.range > 0) {
        const px = Math.round(round.range);
        this.runs.push({
          x: ctx.scene.home.x, y: ctx.scene.home.y, r: round.range,
          text: ctx.reading === 'historical'
            ? S.run_hold_historical : S.run_hold_modern({ kind: round.name, px }),
          angle: r.fig.hold_angle, alpha: t.fig.hold_alpha,
          dash: r.arr.hold_dash, step: t.text.hold,
        });
      }
    }
    if (ctx.aim) this.aim = { x: ctx.aim.x, y: ctx.aim.y, fromX, fromY };
  }

  emit(sinks: ChartSinks, ctx: ChartContext, _frame: FrameInfo): void {
    const t = ctx.cfg.pieces.torpedo_run;
    const r = t[ctx.reading];
    const hist = ctx.reading === 'historical';
    const face = faceOf(ctx.reading);
    const red = FLEET_ORDER_COLORS.attack.color;
    const ink = inkOf(r.ink, red);
    for (const run of this.runs) {
      dashedRing(sinks.circles, run.x, run.y, run.r, t.fig.width, run.dash, red, run.alpha);
      ctx.glyphs.wordAlong(
        sinks.quads, run.text, this.ringPath(run.x, run.y, run.r + t.fig.text_offset),
        face, run.step, ink, r.alpha,
        {
          anchor: 'centre', closed: true,
          startS: (run.r + t.fig.text_offset) * run.angle,
          letterSpacing: r.fig.spacing,
        },
      );
    }
    const aim = this.aim;
    if (!aim) return;
    const fuse = ctx.state.rules.torpedoFusePx;
    sinks.segments.line(
      aim.fromX, aim.fromY, aim.x, aim.y, t.fig.width, red, t.fig.aim_alpha,
      0, t.arr.aim_dash[0], t.arr.aim_dash[1],
    );
    if (fuse > 0) {
      sinks.circles.ring(aim.x, aim.y, fuse, t.fig.width, red, t.fig.ring_alpha);
    }
    const c = t.fig.fuse_cross_r;
    sinks.segments.line(aim.x - c, aim.y, aim.x + c, aim.y, t.fig.width, red, r.alpha);
    sinks.segments.line(aim.x, aim.y - c, aim.x, aim.y + c, t.fig.width, red, r.alpha);
    const px = Math.round(fuse);
    ctx.glyphs.word(
      sinks.quads, hist ? S.aim_historical({ px }) : S.aim_modern({ px }),
      aim.x + t.fig.aim_label_dx, aim.y, face, t.text.main, ink, r.alpha,
    );
  }

  // -- the private half ------------------------------------------------------

  /** Is the selection this commander's own hold? */
  private ownHold(ctx: ChartContext): boolean {
    const sel = ctx.selection;
    if (sel?.kind !== 'site') return false;
    const site = ctx.scene.sites?.get(sel.id);
    if (!site || site.kind !== SiteKind.SITE_KIND_PLAYER_BASE) return false;
    return site.ownerPlayer === ctx.state.playerId;
  }

  /** The polyline a run is lettered along: one closed circle. */
  private ringPath(x: number, y: number, r: number): Pt[] {
    const pts: Pt[] = [];
    for (let i = 0; i < RING_STEPS; i++) {
      const a = (i / RING_STEPS) * Math.PI * 2;
      pts.push({ x: x + Math.cos(a) * r, y: y + Math.sin(a) * r });
    }
    return pts;
  }
}

export const torpedoRun = new TorpedoRunPiece();
